import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Ripple } from './Ripple';
import { Controls } from './Controls';
import { TitleOverlay } from './TitleOverlay';
import { InfoPanel } from './InfoPanel';
import { Fish } from './Fish';
import { Bubble } from './Bubble';
import { Seaweed } from './Seaweed';

// YouTube aquarium videos - User's selected videos
const AQUARIUM_VIDEOS = [
  {
    id: 1,
    youtubeId: 'QCL7VXuO35g', // User's fish tank video 1
    title: 'Relaxing Aquarium'
  },
  {
    id: 2,
    youtubeId: 'vQoR7gJTirk', // User's fish tank video 2
    title: 'Peaceful Fish Tank'
  },
  {
    id: 3,
    youtubeId: 'A67xsfzgSn4', // User's fish tank video 3
    title: 'Calming Waters'
  },
];

const FISH_TYPES = ['goldfish', 'tropical', 'angel', 'clown', 'jelly'];

const generateFish = (count = 12) => {
  const fish = [];
  for (let i = 0; i < count; i++) {
    const type = FISH_TYPES[Math.floor(Math.random() * FISH_TYPES.length)];
    const direction = Math.random() > 0.5 ? 'left' : 'right';
    const speed = 20 + Math.random() * 30;
    const yPosition = 15 + Math.random() * 65;
    const delay = Math.random() * speed;
    const size = 0.8 + Math.random() * 0.5;
    
    fish.push({
      id: `fish-${i}`,
      type, direction, speed, yPosition, delay, size,
      yVariation: (Math.random() - 0.5) * 60,
    });
  }
  return fish;
};

const generateBubbles = (count = 15) => {
  const bubbles = [];
  for (let i = 0; i < count; i++) {
    bubbles.push({
      id: `bubble-${i}`,
      size: 4 + Math.random() * 12,
      left: 5 + Math.random() * 90,
      duration: 10 + Math.random() * 12,
      delay: Math.random() * 10,
    });
  }
  return bubbles;
};

const generateSeaweed = () => [
  { id: 'sw-1', left: 4, height: 120, delay: 0 },
  { id: 'sw-2', left: 8, height: 90, delay: 0.5 },
  { id: 'sw-3', left: 92, height: 110, delay: 0.3 },
  { id: 'sw-4', left: 96, height: 80, delay: 0.7 },
];

export const FishTank = () => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [ripples, setRipples] = useState([]);
  const [isSoundOn, setIsSoundOn] = useState(false);
  const [breathingPhase, setBreathingPhase] = useState('inhale');
  const [useVideo, setUseVideo] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Animated fallback elements
  const [fish] = useState(() => generateFish(12));
  const [bubbles, setBubbles] = useState(() => generateBubbles(15));
  const [seaweed] = useState(() => generateSeaweed());
  
  const tankRef = useRef(null);
  const rippleIdRef = useRef(0);

  // Breathing guide timer
  useEffect(() => {
    const breathingCycle = () => {
      setBreathingPhase('inhale');
      setTimeout(() => setBreathingPhase('hold'), 4000);
      setTimeout(() => setBreathingPhase('exhale'), 7000);
    };
    breathingCycle();
    const interval = setInterval(breathingCycle, 11000);
    return () => clearInterval(interval);
  }, []);

  // Bubble regeneration for animated mode
  useEffect(() => {
    if (!useVideo || videoError) {
      const interval = setInterval(() => {
        const newBubble = {
          id: `bubble-${Date.now()}`,
          size: 4 + Math.random() * 12,
          left: 5 + Math.random() * 90,
          duration: 10 + Math.random() * 12,
          delay: 0,
        };
        setBubbles(prev => [...prev.slice(-20), newBubble]);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [useVideo, videoError]);

  const handleTankClick = useCallback((e) => {
    if (e.target.closest('.controls-container') || e.target.closest('.info-panel')) return;
    
    const rect = tankRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newRipple = { id: `ripple-${rippleIdRef.current++}`, x, y };
    setRipples(prev => [...prev, newRipple]);
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 2000);
  }, []);

  const toggleSound = useCallback(() => setIsSoundOn(prev => !prev), []);
  
  const toggleMode = useCallback(() => {
    setUseVideo(prev => !prev);
    setVideoError(false);
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      tankRef.current?.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.log('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  }, []);

  // Listen for fullscreen changes (e.g., user presses Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const changeVideo = useCallback((direction) => {
    setVideoError(false);
    setCurrentVideoIndex(prev => {
      if (direction === 'next') return (prev + 1) % AQUARIUM_VIDEOS.length;
      return prev === 0 ? AQUARIUM_VIDEOS.length - 1 : prev - 1;
    });
  }, []);

  const currentVideo = AQUARIUM_VIDEOS[currentVideoIndex];
  // YouTube embed URL - autoplay, loop, no controls, starts at 60 seconds (skip first minute)
  const youtubeEmbedUrl = `https://www.youtube.com/embed/${currentVideo.youtubeId}?autoplay=1&mute=${isSoundOn ? 0 : 1}&loop=1&playlist=${currentVideo.youtubeId}&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1&playsinline=1&start=60&vq=hd2160`;

  const showAnimated = !useVideo || videoError;

  return (
    <div ref={tankRef} className="fish-tank" onClick={handleTankClick}>
      {/* Video Mode */}
      {useVideo && !videoError && (
        <div className="video-container">
          <iframe
            key={`${currentVideo.id}-${isSoundOn}`}
            className="youtube-video"
            src={youtubeEmbedUrl}
            title={currentVideo.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onError={() => setVideoError(true)}
          />
        </div>
      )}

      {/* Animated Mode (fallback or selected) */}
      {showAnimated && (
        <>
          <div className="light-rays" />
          <div className="sand-floor" />
          <div className="rock" style={{ left: '15%', width: '70px', height: '40px' }} />
          <div className="rock" style={{ left: '70%', width: '80px', height: '45px' }} />
          <div className="coral" style={{ left: '22%' }}>
            <div className="coral-branch" />
          </div>
          <div className="coral" style={{ left: '75%' }}>
            <div className="coral-branch" style={{ background: 'linear-gradient(180deg, hsl(340 60% 55%) 0%, hsl(350 50% 45%) 100%)' }} />
          </div>
          {seaweed.map(sw => <Seaweed key={sw.id} {...sw} />)}
          {bubbles.map(bubble => <Bubble key={bubble.id} {...bubble} />)}
          {fish.map(f => <Fish key={f.id} {...f} />)}
        </>
      )}

      {/* Overlays */}
      <div className="video-overlay" />
      <div className="water-caustics" />

      {/* Ripples */}
      {ripples.map(ripple => <Ripple key={ripple.id} {...ripple} />)}

      {/* UI */}
      {/* Creator Badge */}
      <a
        className="creator-badge"
        href="https://www.linkedin.com/in/nirmalrajapaksha/"
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <img src="/logo.png" alt="NR Logo" className="creator-logo" />
        <div className="creator-info">
          <span className="creator-label">Created by</span>
          <span className="creator-name">Nirmal Rajapaksha</span>
        </div>
      </a>

      <TitleOverlay breathingPhase={breathingPhase} />
      
      <InfoPanel 
        currentVideo={showAnimated ? 'Animated Fish' : currentVideo.title}
        videoIndex={showAnimated ? 0 : currentVideoIndex + 1}
        totalVideos={showAnimated ? 0 : AQUARIUM_VIDEOS.length}
        isAnimated={showAnimated}
      />
      
      <Controls 
        isSoundOn={isSoundOn}
        toggleSound={toggleSound}
        onPrevVideo={() => changeVideo('prev')}
        onNextVideo={() => changeVideo('next')}
        showNavigation={!showAnimated && AQUARIUM_VIDEOS.length > 1}
        useVideo={useVideo}
        toggleMode={toggleMode}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
      />
    </div>
  );
};
